import { useState, useRef, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Play,
  ThumbsUp,
  ThumbsDown,
  Share2,
  Download,
  Bookmark,
  ChevronDown,
  Check,
  MessageCircle,
  UserPlus,
  User,
  Loader2,
} from 'lucide-react';
import { useVideosFeed } from '@/hooks/useFeed';
import { useComments, useLikePost, useBookmarkPost } from '@/hooks/usePost';
import { useFollowUser } from '@/hooks/useUser';
import { formatCount, formatDuration, timeAgo } from '@/lib/formatters';
import { getVideoSource } from '@/lib/video';
import VideoPlayer from '@/components/VideoPlayer';
import CommentModal from '@/components/CommentModal';
import { getPostUser, getPostImage, getLikesCount } from '@/components/PostCard';
import type { Post } from '@/types';

gsap.registerPlugin(ScrollTrigger);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Video() {

  // UI state
  const [showDescription, setShowDescription] = useState(false);
  const [showComments, setShowComments] = useState(false);
  // Local override for optimistic bookmark UI
  const [bookmarkOverride, setBookmarkOverride] = useState<boolean | null>(null);

  const playerRef = useRef<HTMLDivElement>(null);
  const relatedRef = useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------------

  const {
    data: videosData,
    isLoading: isVideosLoading,
  } = useVideosFeed();

  // Flatten all pages into a single list
  const allVideos = videosData?.pages.flatMap((page) => page.posts) ?? [];

  // First video is the featured one; the rest appear as related
  const featuredPost: Post | undefined = allVideos[0];
  const relatedPosts: Post[] = allVideos.slice(1, 5); // cap at 4 related

  // Derived fields for the featured video
  const featuredAuthor = featuredPost ? getPostUser(featuredPost) : null;
  const featuredVideoSource = featuredPost ? getVideoSource(featuredPost) : null;
  const featuredThumbnail = featuredPost
    ? (featuredVideoSource?.poster || featuredPost.videoUrls?.thumbnail || getPostImage(featuredPost))
    : undefined;
  const featuredLiked = featuredPost?.postEngagement?.liked ?? false;
  const featuredBookmarked = bookmarkOverride ?? Boolean((featuredPost as Record<string, unknown> | undefined)?.isBookmarked);
  const featuredIsFollowing =
    (featuredPost?.user as Record<string, unknown> | undefined)?.isFollowing as boolean | undefined;
  const featuredSubscribersCount =
    (featuredPost?.user as Record<string, unknown> | undefined)?.followersCount as number | undefined;

  // Comments for the featured post (used for teaser preview)
  const { data: commentsData } = useComments(featuredPost?.id);
  const commentsList = commentsData?.pages.flatMap((page) => page.comments) ?? [];

  // Mutations
  const likeMutation = useLikePost();
  const bookmarkMutation = useBookmarkPost();
  const followMutation = useFollowUser();

  // ---------------------------------------------------------------------------
  // Animations
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (relatedRef.current && relatedRef.current.children.length > 0) {
      gsap.fromTo(
        relatedRef.current.children,
        { y: 16, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.4,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: relatedRef.current,
            start: 'top 80%',
          },
        },
      );
    }
  }, [relatedPosts.length]);


  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleLike = useCallback(
    (type: 'like' | 'dislike') => {
      if (!featuredPost) return;

      if (type === 'like') {
        likeMutation.mutate({
          ulid: featuredPost.id,
          liked: featuredLiked,
        });
      }
      // "dislike" is a UI-only toggle — the API only supports like/unlike
    },
    [featuredPost, featuredLiked, likeMutation],
  );

  const handleSubscribe = useCallback(() => {
    if (!featuredAuthor?.username) return;
    followMutation.mutate({
      username: featuredAuthor.username,
      isFollowing: featuredIsFollowing ?? false,
    });
  }, [featuredAuthor, featuredIsFollowing, followMutation]);

  const handleBookmark = useCallback(() => {
    if (!featuredPost) return;

    // Optimistic update
    setBookmarkOverride(!featuredBookmarked);

    bookmarkMutation.mutate(
      { ulid: featuredPost.id, bookmarked: featuredBookmarked },
      {
        onError: () => {
          // Revert on error
          setBookmarkOverride(featuredBookmarked);
        },
        onSettled: () => {
          // Clear override after mutation settles to use server state
          setBookmarkOverride(null);
        },
      }
    );
  }, [featuredPost, featuredBookmarked, bookmarkMutation]);

  const openComments = () => {
    setShowComments(true);
  };

  const closeComments = () => {
    setShowComments(false);
  };

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (isVideosLoading) {
    return (
      <div className="min-h-screen bg-[#0B0D10] flex items-center justify-center">
        <Loader2 size={36} className="animate-spin text-[#A38BFF]" />
      </div>
    );
  }

  if (!featuredPost) {
    return (
      <div className="min-h-screen bg-[#0B0D10] flex items-center justify-center">
        <div className="text-center">
          <Play size={48} className="text-[#2A2F3A] mx-auto mb-3" />
          <p className="text-[#A7B0C8]">No videos available</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-[#0B0D10]">
      {/* Video Player */}
      <div ref={playerRef} className="w-full bg-black">
        {featuredVideoSource ? (
          <VideoPlayer
            src={featuredVideoSource.src}
            poster={featuredVideoSource.poster}
            aspectRatio="16/9"
            muted={false}
            className="rounded-none"
          />
        ) : (
          <div className="relative w-full aspect-video">
            {featuredThumbnail ? (
              <img
                src={featuredThumbnail}
                alt={featuredPost.title ?? 'Video thumbnail'}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-[#141821] flex items-center justify-center">
                <Play size={48} className="text-[#A7B0C8]" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Video info */}
      <div className="px-4 py-4">
        {/* Title */}
        <h1 className="text-lg font-semibold text-[#F5F7FF] leading-tight mb-2">
          {featuredPost.title || featuredPost.content || 'Untitled'}
        </h1>

        {/* Meta */}
        <div className="flex items-center gap-2 text-sm text-[#A7B0C8] mb-4">
          <span>{formatCount(featuredPost.postEngagement?.viewCount ?? 0)} views</span>
          {featuredPost.createdAt && (
            <>
              <span>•</span>
              <span>{timeAgo(featuredPost.createdAt)}</span>
            </>
          )}
        </div>

        {/* Channel row */}
        <div className="flex items-center gap-3 mb-4">
          {featuredAuthor?.avatar ? (
            <img
              src={featuredAuthor.avatar}
              alt={featuredAuthor.name}
              className="w-11 h-11 rounded-full object-cover"
            />
          ) : (
            <div className="w-11 h-11 rounded-full bg-[#A38BFF]/20 flex items-center justify-center">
              <User size={20} className="text-[#A38BFF]" />
            </div>
          )}
          <div className="flex-1">
            <p className="font-semibold text-[#F5F7FF]">{featuredAuthor?.name ?? 'Unknown'}</p>
            {featuredSubscribersCount != null && (
              <p className="text-xs text-[#A7B0C8]">
                {formatCount(featuredSubscribersCount)} subscribers
              </p>
            )}
          </div>
          <button
            onClick={handleSubscribe}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              featuredIsFollowing
                ? 'bg-[#141821] text-[#A7B0C8] border border-white/[0.08]'
                : 'bg-[#A38BFF] text-[#0B0D10]'
            }`}
          >
            {featuredIsFollowing ? (
              <span className="flex items-center gap-1">
                <Check size={16} /> Subscribed
              </span>
            ) : (
              'Subscribe'
            )}
          </button>
        </div>

        {/* Action bar */}
        <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-4">
          {/* Like */}
          <button
            onClick={() => handleLike('like')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              featuredLiked
                ? 'bg-[#A38BFF]/20 text-[#A38BFF]'
                : 'bg-[#141821] text-[#F5F7FF]'
            }`}
            aria-label="Like"
          >
            <ThumbsUp size={18} fill={featuredLiked ? '#A38BFF' : 'none'} />
            <span>{formatCount(getLikesCount(featuredPost))}</span>
          </button>

          {/* Dislike (UI-only — no API equivalent) */}
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap bg-[#141821] text-[#F5F7FF]"
            aria-label="Dislike"
          >
            <ThumbsDown size={18} />
          </button>

          {/* Comments */}
          <button
            onClick={openComments}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#141821] text-[#F5F7FF] text-sm font-medium whitespace-nowrap"
            aria-label="Comments"
          >
            <MessageCircle size={18} />
            <span>{formatCount(featuredPost.commentCount ?? 0)}</span>
          </button>

          {/* Share */}
          <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#141821] text-[#F5F7FF] text-sm font-medium whitespace-nowrap">
            <Share2 size={18} />
            <span>Share</span>
          </button>

          {/* Download */}
          <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#141821] text-[#F5F7FF] text-sm font-medium whitespace-nowrap">
            <Download size={18} />
            <span>Download</span>
          </button>

          {/* Bookmark / Save */}
          <button
            onClick={handleBookmark}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              featuredBookmarked
                ? 'bg-[#A38BFF]/20 text-[#A38BFF]'
                : 'bg-[#141821] text-[#F5F7FF]'
            }`}
            aria-label={featuredBookmarked ? 'Remove bookmark' : 'Save'}
          >
            <Bookmark size={18} fill={featuredBookmarked ? '#A38BFF' : 'none'} />
            <span>Save</span>
          </button>
        </div>

        {/* Description */}
        {(featuredPost.content || featuredPost.title) && (
          <div className="bg-[#141821] rounded-xl p-4 mb-4">
            <button
              onClick={() => setShowDescription(!showDescription)}
              className="w-full flex items-center justify-between"
            >
              <p
                className={`text-sm text-[#A7B0C8] text-left ${showDescription ? '' : 'line-clamp-2'}`}
              >
                {featuredPost.content || featuredPost.title}
              </p>
              <ChevronDown
                size={18}
                className={`text-[#A7B0C8] flex-shrink-0 ml-2 transition-transform ${
                  showDescription ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
        )}

        {/* Comments teaser */}
        <div
          onClick={openComments}
          className="bg-[#141821] rounded-xl p-4 mb-4 cursor-pointer"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openComments(); }}
          aria-label="Open comments"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[#F5F7FF]">
              Comments {formatCount(featuredPost.commentCount ?? 0)}
            </h3>
            <ChevronDown size={18} className="text-[#A7B0C8]" />
          </div>

          {commentsList.length > 0 ? (
            (() => {
              const firstComment = commentsList[0];
              if (!firstComment) return null;
              const firstCommentAuthor = getPostUser(firstComment);
              return (
                <div className="flex items-start gap-3">
                  {firstCommentAuthor.avatar ? (
                    <img
                      src={firstCommentAuthor.avatar}
                      alt={firstCommentAuthor.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#A38BFF]/20 flex items-center justify-center">
                      <User size={14} className="text-[#A38BFF]" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-[#A7B0C8]">
                      <span className="text-[#F5F7FF] font-medium">
                        @{firstCommentAuthor.username || firstCommentAuthor.name}
                      </span>{' '}
                      {firstComment.body}
                    </p>
                  </div>
                </div>
              );
            })()
          ) : (
            <p className="text-sm text-[#A7B0C8]">Be the first to comment</p>
          )}
        </div>

        {/* Related videos */}
        {relatedPosts.length > 0 && (
          <div>
            <h3 className="font-semibold text-[#F5F7FF] mb-3">Related videos</h3>
            <div ref={relatedRef} className="grid grid-cols-2 gap-3">
              {relatedPosts.map((video) => {
                const videoAuthor = getPostUser(video);
                const videoThumbnail = video.videoUrls?.thumbnail ?? getPostImage(video);
                const videoIsFollowing =
                  (video.user as Record<string, unknown> | undefined)?.isFollowing as
                    | boolean
                    | undefined;

                return (
                  <div key={video.id} className="bg-[#141821] rounded-xl overflow-hidden">
                    <div className="relative">
                      {videoThumbnail ? (
                        <img
                          src={videoThumbnail}
                          alt={video.title ?? 'Video'}
                          className="w-full aspect-video object-cover"
                        />
                      ) : (
                        <div className="w-full aspect-video bg-[#0B0D10] flex items-center justify-center">
                          <Play size={24} className="text-[#A7B0C8]" />
                        </div>
                      )}
                      {video.duration != null && (
                        <span className="absolute bottom-2 right-2 bg-black/60 px-1.5 py-0.5 rounded text-xs text-white">
                          {formatDuration(video.duration)}
                        </span>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-medium text-[#F5F7FF] line-clamp-2 mb-1">
                        {video.title || video.content || 'Untitled'}
                      </h4>
                      <p className="text-xs text-[#A7B0C8]">{videoAuthor.name}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-[#A7B0C8]">
                          {formatCount(video.postEngagement?.viewCount ?? 0)} views
                          {video.createdAt ? ` • ${timeAgo(video.createdAt)}` : ''}
                        </p>
                        {videoAuthor.username && (
                          <button
                            onClick={() =>
                              followMutation.mutate({
                                username: videoAuthor.username,
                                isFollowing: videoIsFollowing ?? false,
                              })
                            }
                            className="text-[#A38BFF]"
                            aria-label={videoIsFollowing ? 'Unfollow' : 'Follow'}
                          >
                            {videoIsFollowing ? (
                              <Check size={14} />
                            ) : (
                              <UserPlus size={14} />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Comments Modal */}
      {showComments && featuredPost && (
        <CommentModal
          postUlid={featuredPost.id}
          onClose={closeComments}
        />
      )}
    </div>
  );
}
